import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames/bind';
import noop from 'lodash/noop';
import getBrowserHistory from '../../navigation/history/getBrowserHistory';
import * as styles from './ImagemapBannerResponsive.css';
import Icon from '../Icon/Icon';
import Tag from '../../assets/svg/Tag.svg';
import { checkUrlExtension } from '../../helpers/Utils/Utils';
import { dt } from '../../helpers/Utils/GetTailwindToken';

const cx = classNames.bind(styles);

/**
 * This component to standardize the styling and html structure of any advertisement/promotion image banner with area map.
 * It is responsive. The co-ordinates for area will be recalculated based on the image width.
 *
 */

class ImagemapBannerResponsive extends Component {
    /**
     * Supported React properties
     * @type {Object}
     */
    static propTypes = {
        /**
         * Unique name for referencing dom element in automation testing
         * @type {String}
         */
        automationId: PropTypes.string,

        /**
         * source path of the banner image
         * @type {string}
         */
        imageUrl: PropTypes.string,

        /**
         * an array containig details to feed multiple HTML area attribute
         * @type {array}
         */
        imagemapArea: PropTypes.array,

        /**
         * alternative text for the banner image
         * @type {string}
         */
        imageAltText: PropTypes.string,
        /**  If isLazy is true set loading to lazy, else null
         */
        isLazy: PropTypes.bool,

        softRoute: PropTypes.bool,
        onImageLoad: PropTypes.func,
        onImageError: PropTypes.func,
        theme: PropTypes.string,
        hasPin: PropTypes.bool,
        onClick: PropTypes.func,
        location: PropTypes.string,
        imageWidth: PropTypes.string,
        imageHeight: PropTypes.string,
        CMcomponent: PropTypes.bool,
    };

    static defaultProps = {
        automationId: 'imgbnr',
        imageUrl: '',
        imagemapArea: [],
        imageAltText: 'JCP image',
        softRoute: false,
        onImageLoad: () => {},
        onImageError: () => {},
        theme: '',
        hasPin: false,
        onClick: noop,
        location: '',
        imageWidth: '',
        imageHeight: '',
        isLazy: false,
    };

    /* istanbul ignore next */
    constructor(props) {
        super(props);
        this.state = {
            showPin: false,
        };
        this.goToPage = this.goToPage.bind(this);
        this.handleImageLoaded = this.handleImageLoaded.bind(this);
        this.handleEvent = this.handleEvent.bind(this);
    }

    componentDidMount() {
        const currentImage = this.image;
        if (currentImage && currentImage.complete) {
            this.handleImageLoaded(true);
        }
        /* istanbul ignore next */
        window.addEventListener(
            'resize',
            () => {
                this.handleImageLoaded();
            },
            false
        );
    }

    componentWillUnmount() {
        /* istanbul ignore next */
        window.removeEventListener(
            'resize',
            () => {
                this.handleImageLoaded();
            },
            false
        );
    }

    handleEvent(targetValue = '_self') {
        /** creating a closure to
         * pass event with additional params
         * */
        /* istanbul ignore next */
        return (e) => {
            this.goToPage(e, targetValue);
        };
    }

    goToPage(e, targetAttr = '_self') {
        e.preventDefault();
        let analyticsTrackCode = e.target.dataset.analyticstag;
        let softRouteURL = e.target.dataset.softlink;

        try {
            /* istanbul ignore next */
            if (!softRouteURL || typeof softRouteURL === 'undefined') {
                softRouteURL = e.target.getAttribute('data-soft-link');
                analyticsTrackCode = e.target.getAttribute('data-analytics-tag');
            }
        } catch (ev) {
            console.log('data attribute issue');
        }

        const matchForCmRe = /cm_re/.test(softRouteURL.toLowerCase());
        /* istanbul ignore next */
        if (!matchForCmRe && analyticsTrackCode) {
            const match = /.+\?.*/.test(softRouteURL);
            if (match) {
                softRouteURL += `&${analyticsTrackCode}`;
            } else {
                softRouteURL += `?${analyticsTrackCode}`;
            }
        }

        /* istanbul ignore next */
        if (this.props.softRoute) {
            if (e.target.dataset.leaf === 'true' || e.target.dataset.leaf === true) {
                window.open(softRouteURL, targetAttr);
            } else {
                getBrowserHistory().push(softRouteURL);
            }
        } else {
            window.open(softRouteURL, targetAttr);
        }
    }

    createMapArea(imagemapArea) {
        const { hasPin, onClick } = this.props;
        return imagemapArea.map((args) => (
            <area
                key={args.coords.split(',').join('')}
                shape={args.shape}
                coords={args.coords}
                data-soft-link={args.link}
                data-leaf={args.leaf}
                data-analytics-tag={args.analyticsTag}
                onClick={hasPin ? () => onClick(args.ppid) : this.handleEvent(args.target)}
                alt={args.alt}
                title={args.alt}
                target={args.target}
                href={args.link}
            />
        ));
    }

    placeMapIcons(imagemapArea) {
        const { onClick, location, CMcomponent } = this.props;
        return imagemapArea.map((args) => {
            // inline styles to dynamicall generate top and left positions based on coords
            const coordsArray = args.coords.split(',');
            const uniqueId = `${location}-${args.ppid}`;
            const imagemapBannerIconStyle = dt([
                'absolute',
                'h-10',
                'w-10',
                'flex',
                'justify-center',
                'items-center',
                'hover:cursor-pointer',
            ]);
            const svgStyle = dt(['h-8', 'w-8', 'lg:h-9', 'lg:w-9']);
            const hideBtnTextStyle = dt(['hidden']);
            const imagemapBannerBlockStyle = dt(['absolute']);
            return (
                <div
                    className={cx('imagemapBannerBlock', imagemapBannerBlockStyle)}
                    key={coordsArray.join('')}
                >
                    {this.state.showPin && (
                        <div
                            className={
                                CMcomponent
                                    ? `${cx('CMimagemapBannerIconContainer')}`
                                    : `${cx('imagemapBannerIconContainer')}`
                            }
                            id={uniqueId}
                        >
                            <button
                                type="button"
                                className={
                                    CMcomponent
                                        ? `${cx('CMimagemapBannerIcon')} ${imagemapBannerIconStyle}`
                                        : `${cx('imagemapBannerIcon')} ${imagemapBannerIconStyle}`
                                }
                                onClick={() => onClick(args.ppid)}
                                data-analytics-tag={args.analyticsTag}
                            >
                                <Icon
                                    className={cx('tag', 'default', svgStyle)}
                                    iconType="svg"
                                    automationId="test-automation-icon"
                                    width="35px"
                                    height="35px"
                                    viewBox="0 0 35 35"
                                    pathClassName={styles.iconPath}
                                >
                                    <Tag />
                                </Icon>
                                <span className={cx('hideBtnText', hideBtnTextStyle)}>
                                    Shoppable Image
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            );
        });
    }

    handleImageLoaded(imageLoaded = false) {
        if (!__SERVER__) {
            const { naturalWidth } = this.image || {};
            const { naturalHeight } = this.image || {};
            const areas = (this.imagemap || {}).children;
            const areaLength = (areas || {}).length;
            const imageWrapper = this.imageWrapper || {};
            const coords = [];
            const scaleWidth = imageWrapper.clientWidth / naturalWidth;
            const scaleHeight = imageWrapper.clientHeight / naturalHeight;
            const icons = (this.mapIcons || {}).children;
            let iteratorX = 0;
            let iteratorY = 0;
            let selScaeUnit = 0;

            for (iteratorX = 0; iteratorX < areaLength; iteratorX += 1) {
                /* getting the actual map area co-ordinates */
                coords[iteratorX] = this.props.imagemapArea[iteratorX].coords.split(',');
                for (iteratorY = 0; iteratorY < coords[iteratorX].length; iteratorY += 1) {
                    selScaeUnit = iteratorY % 2 ? scaleHeight : scaleWidth;
                    /* multiplying the actual map area co-ordinates with scaleUnit */
                    coords[iteratorX][iteratorY] *= selScaeUnit;
                }
                /* updating the DOM */
                areas[iteratorX].coords = coords[iteratorX].join(',');
                if (this.props.hasPin) {
                    icons[iteratorX].style.left = `${coords[iteratorX][0] - 20}px`;
                    icons[iteratorX].style.top = `${coords[iteratorX][1] - 20}px`;
                }

                if (!this.state.showPin && imageLoaded) {
                    this.setState({ showPin: true });
                }
            }
        }
    }

    render() {
        const {
            automationId,
            onImageLoad,
            onImageError,
            theme,
            imagemapArea,
            hasPin,
            imageUrl,
            imageWidth,
            imageHeight,
            isLazy,
        } = this.props;
        const uniqueId = `${Date.now()}-${Math.floor(Math.random() * 10000 + 1)}`;
        const loading = isLazy ? 'lazy' : null;
        const imageExt = checkUrlExtension(imageUrl);
        const imagemapBannerResponsiveStyle = dt(['relative', 'overflow-hidden']);
        return (
            <div
                className={cx('imagemapBannerResponsive', imagemapBannerResponsiveStyle, theme)}
                ref={(wrapper) => {
                    this.imageWrapper = wrapper;
                }}
                data-automation-id={automationId}
            >
                {imageExt === 'mp4' ? (
                    <video playsInline autoPlay muted loop width="100%" height="100%">
                        <source src={this.props.imageUrl} type="video/mp4" />
                    </video>
                ) : (
                    <img
                        loading={loading}
                        src={this.props.imageUrl}
                        alt={this.props.imageAltText}
                        title={this.props.imageAltText}
                        width={imageWidth}
                        height={imageHeight}
                        useMap={`#${uniqueId}`}
                        onLoad={() => {
                            this.handleImageLoaded(true);
                            onImageLoad();
                        }}
                        onError={onImageError}
                        className={dt(['w-full', 'h-auto'])}
                        ref={(img) => {
                            this.image = img;
                        }}
                    />
                )}

                <map
                    name={uniqueId}
                    ref={(map) => {
                        this.imagemap = map;
                    }}
                >
                    {this.createMapArea(imagemapArea)}
                </map>
                <div
                    ref={(pins) => {
                        this.mapIcons = pins;
                    }}
                >
                    {hasPin && this.placeMapIcons(imagemapArea)}
                </div>
            </div>
        );
    }
}

export default ImagemapBannerResponsive;
